import { ZERO } from "constants/coupons";

export const mapVerticalOption = (vertical) => ({
  id: vertical.id,
  label: vertical.name ?? String(vertical.id),
});

export const mapCurrencyOption = (currency) => {
  const displayName =
    currency.display_name ?? currency.displayName ?? "";
  const code = String(currency.code ?? "").trim();
  const name = currency.name ?? "";

  const label = displayName
    ? code
      ? `${displayName} (${code})`
      : displayName
    : name || code || String(currency.id);

  return {
    id: currency.id,
    label,
    code,
    name,
  };
};

export const resolveCurrencyCodeFromOptions = (currencyOptions, currencyId) => {
  if (
    currencyId === "" ||
    currencyId === null ||
    currencyId === undefined
  ) {
    return "";
  }

  const matchingCurrency = currencyOptions.find(
    (option) => String(option.id) === String(currencyId)
  );

  return String(matchingCurrency?.code ?? "").trim();
};

export const resolveTenantCurrencyId = (tenant) => {
  const singularCurrencyId = tenant?.currency_id ?? tenant?.currencyId;
  if (
    singularCurrencyId !== null &&
    singularCurrencyId !== undefined &&
    String(singularCurrencyId).trim() !== ""
  ) {
    return singularCurrencyId;
  }

  const currencyIds = tenant?.currency_ids ?? tenant?.currencyIds ?? [];
  if (!Array.isArray(currencyIds) || currencyIds.length === ZERO) {
    return null;
  }

  const firstCurrencyId = currencyIds.find(
    (currencyId) =>
      currencyId !== null &&
      currencyId !== undefined &&
      String(currencyId).trim() !== ""
  );

  return firstCurrencyId ?? null;
};

export const resolveTenantCurrencyIds = (tenant) => {
  const currencyIds = tenant?.currency_ids ?? tenant?.currencyIds;
  if (Array.isArray(currencyIds) && currencyIds.length > ZERO) {
    return [
      ...new Set(
        currencyIds
          .filter(
            (currencyId) =>
              currencyId !== null &&
              currencyId !== undefined &&
              String(currencyId).trim() !== ""
          )
          .map((currencyId) => String(currencyId))
      ),
    ];
  }

  const singularCurrencyId = resolveTenantCurrencyId(tenant);
  return singularCurrencyId === null ? [] : [String(singularCurrencyId)];
};

export const mapTenantOption = (tenant) => {
  const currencyIds = resolveTenantCurrencyIds(tenant);

  return {
    id: tenant.id,
    label: tenant.name ?? tenant.tenant_code ?? `Tenant ${tenant.id ?? ""}`,
    tenantCode: tenant.tenant_code ?? tenant.tenantCode ?? null,
    currencyId: resolveTenantCurrencyId(tenant),
    currencyIds,
    countryId: tenant.country_id ?? tenant.countryId ?? null,
    timezoneId: tenant.timezone_id ?? tenant.timezoneId ?? null,
  };
};

export const enrichTenantOptionsWithCurrency = (
  tenantOptions,
  currencyOptions = []
) =>
  tenantOptions.map((option) => ({
    ...option,
    currencyCode: resolveCurrencyCodeFromOptions(
      currencyOptions,
      option.currencyId
    ),
  }));

const formatTenantOptionLabel = (
  option,
  { includeCurrencyCode = false } = {}
) => {
  const baseLabel = option.label ?? `Tenant ${option.id ?? ""}`;

  if (!includeCurrencyCode) {
    return baseLabel;
  }

  const currencyCode = String(option.currencyCode ?? "").trim();
  if (currencyCode === "") {
    return baseLabel;
  }

  return `${baseLabel} (${currencyCode})`;
};

export const getSelectedTenantCurrencyCodes = (
  tenantOptions,
  tenantIds,
  currencyOptions = []
) => {
  const enrichedOptions = enrichTenantOptionsWithCurrency(
    tenantOptions,
    currencyOptions
  );

  return [
    ...new Set(
      normalizeTenantIdList(tenantIds)
        .map(
          (tenantId) =>
            enrichedOptions.find((option) => String(option.id) === tenantId)
              ?.currencyCode ?? ""
        )
        .map((currencyCode) => String(currencyCode).trim())
        .filter((currencyCode) => currencyCode.length > ZERO)
    ),
  ];
};

export const getTenantOptionDisplayLabels = (
  tenantOptions,
  selectedIds,
  { includeCurrencyCode = false } = {}
) => {
  const normalizedSelectedIds = normalizeTenantIdList(selectedIds);

  if (normalizedSelectedIds.length === ZERO) {
    return null;
  }

  const labels = normalizedSelectedIds.map((selectedId) => {
    const matchingOption = tenantOptions.find(
      (option) => String(option.id) === String(selectedId)
    );

    if (!matchingOption) {
      return String(selectedId);
    }

    return formatTenantOptionLabel(matchingOption, { includeCurrencyCode });
  });

  return labels.join(", ");
};

export const buildCouponFormTenantOptions = ({
  hasDiscountType,
  hasSelectedVertical,
  tenantIds,
  enrichedTenantsForVertical,
  apiTenantOptions = [],
}) => {
  if (!hasDiscountType) {
    return mergeTenantOptionsWithSelectedIds([], tenantIds, []);
  }

  if (!hasSelectedVertical) {
    return mergeTenantOptionsWithSelectedIds(
      apiTenantOptions,
      tenantIds,
      apiTenantOptions
    );
  }

  return mergeTenantOptionsWithSelectedIds(
    enrichedTenantsForVertical,
    tenantIds,
    enrichedTenantsForVertical
  );
};

export const normalizeTenantIdList = (tenantIds) => {
  let normalizedIds = [];

  if (Array.isArray(tenantIds)) {
    normalizedIds = tenantIds
      .map((tenantId) => String(tenantId ?? "").trim())
      .filter((tenantId) => tenantId.length > ZERO);
  } else if (
    tenantIds !== null &&
    tenantIds !== undefined &&
    tenantIds !== ""
  ) {
    const normalizedTenantId = String(tenantIds).trim();
    if (normalizedTenantId.length > ZERO) {
      normalizedIds = [normalizedTenantId];
    }
  }

  return [...new Set(normalizedIds)];
};

const mergeTenantOptionsWithSelectedIds = (
  tenantOptions,
  selectedTenantIds,
  allTenantsForVertical = []
) => {
  const mergedOptions = [...tenantOptions];
  const existingOptionIds = new Set(
    mergedOptions.map((option) => String(option.id))
  );

  normalizeTenantIdList(selectedTenantIds).forEach((selectedTenantId) => {
    if (existingOptionIds.has(selectedTenantId)) {
      return;
    }

    const matchingTenant = allTenantsForVertical.find(
      (tenant) => String(tenant.id) === selectedTenantId
    );

    mergedOptions.push(
      matchingTenant ?? {
        id: selectedTenantId,
        label: `Tenant ${selectedTenantId}`,
      }
    );
    existingOptionIds.add(selectedTenantId);
  });

  return mergedOptions;
};

export const getTenantOptionIds = (tenantOptions) =>
  tenantOptions.map((option) => String(option.id));

export const mergeCurrencyOptionsWithTenantDetails = (
  currencyOptions = [],
  tenantDetails = []
) => {
  const optionsById = new Map(
    currencyOptions.map((option) => [String(option.id), option])
  );

  tenantDetails.forEach((tenant) => {
    const currencyId = String(tenant.currencyId ?? "").trim();
    if (currencyId === "" || optionsById.has(currencyId)) {
      return;
    }

    const currencyCode = String(tenant.currencyCode ?? "").trim();
    optionsById.set(currencyId, {
      id: tenant.currencyId,
      code: currencyCode,
      name: currencyCode,
      label: currencyCode || `Currency ${currencyId}`,
    });
  });

  return [...optionsById.values()];
};

export const getOptionLabel = (options, selectedId) => {
  if (selectedId === "" || selectedId === null || selectedId === undefined) {
    return null;
  }

  return (
    options.find((option) => String(option.id) === String(selectedId))?.label ??
    String(selectedId)
  );
};
